import React, { useMemo, useRef, useState } from "react";
import {
    Button,
    Popper,
    Grow,
    ClickAwayListener,
    Paper,
    Tooltip,
    Stack,
    TextField,
    Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import "./DateFilterStyle.css";

import { nb } from "date-fns/locale";
import { format, parse, isValid, startOfDay } from "date-fns";
import {bool, boolean} from "yup";
const DATE_FMT = "dd.MM.yyyy";

// Convert date to Norwegian-format as long as the input isn't null.
function toStartOfDay(d) {
    return d ? startOfDay(d) : null;
}

function parseNorwegianDate(value) {
    const d = parse(value, DATE_FMT, new Date());
    return isValid(d) ? toStartOfDay(d) : null;
}

function fmt(d) {
    return d ? format(d, DATE_FMT) : "";
}

export default function DateFilter({ onDateChange }) {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const [calendarKey, setCalendarKey] = useState(0);
    
    const [range, setRange] = useState(undefined);

    // input strings (so user can type partial stuff)
    const [fromText, setFromText] = useState("");
    const [toText, setToText] = useState("");

    // month shown in calendar (1 month only)
    // const [month, setMonth] = useState(new Date());

    const hasCompleteRange = !!range?.from && !!range?.to;

    const buttonLabel = useMemo(() => {
        if (hasCompleteRange) return `${fmt(range.from)} – ${fmt(range.to)}`;
        return "Dato";
    }, [hasCompleteRange, range]);

    const handleToggle = () => setOpen((p) => !p);

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event?.target)) return;
        setOpen(false);
    };

    const applyRange = (from, to) => {
        // Only activate filter when both exist
        if (from && to) onDateChange?.(from, to);
        else onDateChange?.(null, null);
    };

    const handleClear = () => {
        setRange(undefined);
        setFromText("");
        setToText("");
        applyRange(null, null);
        // keep open or close—your choice
        // setOpen(false);
    };

    const handleSelect = (next) => {
        setRange(next);

        // keep inputs in sync
        setFromText(next?.from ? fmt(next.from) : "");
        setToText(next?.to ? fmt(next.to) : "");

        applyRange(next?.from ?? null, next?.to ?? null);
    };
    
    // region Handle To / From changes
    // manual input handling
    const handleFromChange = (value, process) => {
        setFromText(value);

        if(!process)
            return;
        
        const from = parseNorwegianDate(value);

        // If invalid typed date, don't overwrite selection yet
        if (value.trim() !== "" && !from) return;

        const nextRange = {
            from: value.trim() === "" ? undefined : from,
            to: range?.to ?? undefined,
        };

        postProcessDateChange(nextRange);
    };
    
    const handleToChange = (value, process) => {
        setToText(value);

        if(!process)
            return;
         
        const to = parseNorwegianDate(value);

        if (value.trim() !== "" && !to) return;

        const nextRange = {
            from: range?.from ?? undefined,
            to: value.trim() === "" ? undefined : to,
        };

        postProcessDateChange(nextRange);
    };

    const postProcessDateChange = (nextRange) => {
        let { from, to } = nextRange;

        // If both exist and are reversed, fix it
        if (from && to && to < from) {
            const tmp = from;
            from = to;
            to = tmp;

            // keep the text fields consistent too
            setFromText(fmt(from));
            setToText(fmt(to));
        }
        
        const cleaned = { from, to };

        setRange(cleaned.from || cleaned.to ? cleaned : undefined);
        applyRange(cleaned.from ?? null, cleaned.to ?? null);
    }
    // endregion

    return (
        <>
            <Tooltip title="Vis kurs med aktivitet i det angitte tidsrommet." arrow>
                <Button
                    className="FilterButton"
                    endIcon={<ExpandMoreIcon />}
                    ref={anchorRef}
                    onClick={handleToggle}
                    sx={{
                        mx: 3,
                        px: 2,
                        "@media (max-width: 600px)": { mx: 1, px: 1 },
                        "@media (max-width: 400px)": { mx: 0, px: 0 },
                        maxWidth: "220px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {buttonLabel}
                </Button>
            </Tooltip>

            <Popper
                open={open}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                transition
                disablePortal
                sx={{ zIndex: "tooltip" }}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === "bottom-start" ? "left top" : "left bottom",
                        }}
                    >
                        <Paper sx={{ borderRadius: 2, overflow: "hidden", width: 360 }}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <div style={{ padding: 12 }}>
                                    {/* Inputs row */}
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <ClickAwayListener onClickAway={() => handleFromChange(fromText, true)}>
                                            <TextField
                                                label="Fra"
                                                value={fromText}
                                                onChange={(e) => handleFromChange(e.target.value, false)}
                                                placeholder="dd.mm.åååå"
                                                size="small"
                                                fullWidth
                                                inputProps={{ inputMode: "numeric" }}
                                                
                                                onBlur={() => handleFromChange(fromText, true)}
    
                                                // User presses Enter
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        // handleFromChange(fromText, true);
                                                        e.target.blur(); // optional: removes focus
                                                    }
                                                }}
                                            />
                                        </ClickAwayListener>
                                        <TextField
                                            label="Til"
                                            value={toText}
                                            onChange={(e) => handleToChange(e.target.value, false)}
                                            placeholder="dd.mm.åååå"
                                            size="small"
                                            fullWidth
                                            inputProps={{ inputMode: "numeric" }}

                                            onBlur={() => handleToChange(toText, true)}
                                            
                                            // User presses Enter
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    // handleToChange(toText, true);
                                                    e.target.blur(); // optional: removes focus
                                                }
                                            }}
                                        />
                                    </Stack>

                                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                                        <Button size="small" onClick={handleClear}>
                                            Nullstill
                                        </Button>
                                    </Stack>

                                    <Divider sx={{ my: 1 }} />

                                    {/* Calendar */}
                                    <div className="mvPicker">
                                        <DayPicker
                                            key={calendarKey}
                                            mode="range"
                                            selected={range}
                                            onSelect={handleSelect}
                                            defaultMonth={range?.from ?? new Date()}   // initial focus only
                                            numberOfMonths={1}
                                            locale={nb}
                                            weekStartsOn={1}
                                            fixedWeeks
                                            showOutsideDays
                                            disabled={{
                                                before: new Date("2023-01-01"),
                                                after: new Date("2030-12-31"),
                                            }}
                                        />
                                    </div>
                                </div>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
}